/**
 * Settings page - Full auth integration
 * 
 * Per design.md: Use @uprosal/ui components, semantic tokens, lucide icons
 * 
 * Security section includes:
 * - Connected accounts (GitHub, Google, Password)
 * - Password management (change/set)
 * - Email verification status
 * - Sign out of all devices
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useAction } from 'convex/react';
import { useAuthActions } from '@convex-dev/auth/react';
import { api } from '../../../../../../convex/_generated/api';
import { motion } from 'framer-motion';
import {
    Card, CardContent, CardHeader, CardTitle,
    Button, Badge, Input, Select,
    User, Bell, Shield, Link as LinkIcon, FileText,
} from '@uprosal/ui';
import { Mail, Check, X, Key, LogOut, AlertCircle, Eye, EyeOff } from 'lucide-react';

// Google Icon SVG component
function GoogleIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
        </svg>
    );
}

// Toggle switch component
function Toggle({ checked, onChange }: { checked: boolean; onChange: (val: boolean) => void }) {
    return (
        <button
            type="button"
            onClick={() => onChange(!checked)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? 'bg-primary' : 'bg-muted'
                }`}
        >
            <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'
                    }`}
            />
        </button>
    );
}

// Change Password Modal
function ChangePasswordModal({
    isOpen,
    onClose,
    isSettingNew
}: {
    isOpen: boolean;
    onClose: () => void;
    isSettingNew: boolean;
}) {
    const { signIn } = useAuthActions();
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPasswords, setShowPasswords] = useState(false);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const handleSubmit = async () => {
        setError('');
        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }
        if (newPassword.length < 8) {
            setError('Password must be at least 8 characters');
            return;
        }

        setIsLoading(true);
        try {
            // For setting new password, we use the signUp flow to add password auth
            // For changing password, we need to verify current first
            if (isSettingNew) {
                await signIn('password', {
                    email: '', // Will be filled from session
                    password: newPassword,
                    flow: 'signUp',
                });
            } else {
                // Note: Convex Auth doesn't have a built-in change password flow
                // You'd typically verify current password then update
                // For now, we'll show success and handle via password reset
                setError('Use "Forgot Password" on sign-in page to change your password');
                setIsLoading(false);
                return;
            }
            setSuccess(true);
            setTimeout(() => {
                onClose();
                setSuccess(false);
            }, 2000);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to update password');
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md mx-4">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Key className="w-5 h-5 text-primary" />
                        {isSettingNew ? 'Set a Password' : 'Change Password'}
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    {success ? (
                        <div className="text-center py-4">
                            <Check className="w-12 h-12 text-primary mx-auto mb-2" />
                            <p className="text-foreground font-medium">Password updated successfully!</p>
                        </div>
                    ) : (
                        <>
                            {error && (
                                <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm flex items-center gap-2">
                                    <AlertCircle className="w-4 h-4" />
                                    {error}
                                </div>
                            )}
                            {!isSettingNew && (
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">
                                        Current Password
                                    </label>
                                    <div className="relative">
                                        <Input
                                            type={showPasswords ? 'text' : 'password'}
                                            value={currentPassword}
                                            onChange={(e) => setCurrentPassword(e.target.value)}
                                            placeholder="â€¢â€¢â€¢â€¢â€¢â€¢â€¢â€¢"
                                        />
                                    </div>
                                </div>
                            )}
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">
                                    {isSettingNew ? 'Password' : 'New Password'}
                                </label>
                                <div className="relative">
                                    <Input
                                        type={showPasswords ? 'text' : 'password'}
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        placeholder="â€¢â€¢â€¢â€¢â€¢â€¢â€¢â€¢"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPasswords(!showPasswords)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                    >
                                        {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">
                                    Confirm Password
                                </label>
                                <Input
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="â€¢â€¢â€¢â€¢â€¢â€¢â€¢â€¢"
                                />
                            </div>
                            <div className="flex gap-3 pt-2">
                                <Button
                                    variant="outline"
                                    onClick={onClose}
                                    className="flex-1"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleSubmit}
                                    disabled={isLoading}
                                    className="flex-1"
                                >
                                    {isLoading ? 'Saving...' : isSettingNew ? 'Set Password' : 'Update Password'}
                                </Button>
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

export function SettingsPage() {
    const navigate = useNavigate();
    const preferences = useQuery(api.queries.preferences.get);
    const connectedProfiles = useQuery(api.queries.profiles.listConnected);
    const linkedAccounts = useQuery(api.queries.authAccounts.listLinkedAccounts);
    const updatePreferences = useMutation(api.mutations.preferences.update);
    const signOutAllDevicesAction = useAction(api.actions.sessions.signOutAllDevices);
    const { signIn } = useAuthActions();

    // Local state for form
    const [displayName, setDisplayName] = useState('');
    const [defaultTone, setDefaultTone] = useState('casual_friendly');
    const [emojiPolicy, setEmojiPolicy] = useState('off');
    const [complianceStrict, setComplianceStrict] = useState(true);
    const [emailUpdates, setEmailUpdates] = useState(true);
    const [usageAlerts, setUsageAlerts] = useState(true);
    const [newFeatures, setNewFeatures] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState('');

    // Password modal state
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [isSettingNewPassword, setIsSettingNewPassword] = useState(false);

    // Session management state
    const [isSigningOutAll, setIsSigningOutAll] = useState(false);

    // Sync state with fetched preferences
    useEffect(() => {
        if (preferences) {
            setDisplayName(preferences.displayName || '');
            setDefaultTone(preferences.defaultTone || 'casual_friendly');
            setEmojiPolicy(preferences.emojiPolicy || 'off');
            setComplianceStrict(preferences.complianceStrict ?? true);
            setEmailUpdates(preferences.emailUpdates ?? true);
            setUsageAlerts(preferences.usageAlerts ?? true);
            setNewFeatures(preferences.newFeatures ?? true);
        }
    }, [preferences]);

    // Scroll to section based on hash
    useEffect(() => {
        const hash = window.location.hash;
        if (hash === '#notifications') {
            setTimeout(() => {
                const element = document.getElementById('notifications');
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 100);
        }
    }, []);

    const handleSave = async () => {
        setIsSaving(true);
        setSaveMessage('');
        try {
            await updatePreferences({
                displayName,
                defaultTone,
                emojiPolicy,
                complianceStrict,
                emailUpdates,
                usageAlerts,
                newFeatures,
            });
            setSaveMessage('Settings saved successfully!');
            setTimeout(() => setSaveMessage(''), 3000);
        } catch (err) {
            setSaveMessage('Failed to save settings');
        } finally {
            setIsSaving(false);
        }
    };

    const handleSignOutAllDevices = async () => {
        setIsSigningOutAll(true);
        try {
            await signOutAllDevicesAction({});
            setSaveMessage('Signed out of all other devices');
            setTimeout(() => setSaveMessage(''), 3000);
        } catch (err) {
            setSaveMessage('Failed to sign out of devices');
        } finally {
            setIsSigningOutAll(false);
        }
    };

    const handleLinkProvider = (provider: string) => {
        // Initiate OAuth linking flow
        void signIn(provider);
    };

    const handleChangePassword = () => {
        setIsSettingNewPassword(false);
        setShowPasswordModal(true);
    };

    const handleSetPassword = () => {
        setIsSettingNewPassword(true);
        setShowPasswordModal(true);
    };

    const profiles = connectedProfiles ?? [];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-foreground">Settings</h1>
                <p className="text-muted-foreground mt-1">
                    Manage your account and preferences
                </p>
            </div>

            {/* Settings sections */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main settings */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Profile */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                    >
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-3 text-lg">
                                    <User className="w-5 h-5 text-primary" />
                                    Profile
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">
                                        Display Name
                                    </label>
                                    <Input
                                        type="text"
                                        value={displayName}
                                        onChange={(e) => setDisplayName(e.target.value)}
                                        placeholder="Your name"
                                    />
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Shown in the top navigation and team view
                                    </p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">
                                        Email
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <Input
                                            type="email"
                                            value={preferences?.email || ''}
                                            disabled
                                            className="bg-muted flex-1"
                                        />
                                        {linkedAccounts?.emailVerified ? (
                                            <Badge variant="success" className="whitespace-nowrap">
                                                <Check className="w-3 h-3 mr-1" />
                                                Verified
                                            </Badge>
                                        ) : (
                                            <Badge variant="outline" className="whitespace-nowrap text-warning">
                                                <AlertCircle className="w-3 h-3 mr-1" />
                                                Not verified
                                            </Badge>
                                        )}
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Email cannot be changed
                                    </p>
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>

                    {/* Proposal Defaults */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.1 }}
                    >
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-3 text-lg">
                                    <FileText className="w-5 h-5 text-primary" />
                                    Proposal Defaults
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">
                                        Default Tone
                                    </label>
                                    <Select
                                        value={defaultTone}
                                        onChange={(e) => setDefaultTone(e.target.value)}
                                    >
                                        <option value="casual_friendly">Casual & Friendly</option>
                                        <option value="strictly_professional">Strictly Professional</option>
                                        <option value="creative_engaging">Creative & Engaging</option>
                                        <option value="enthusiastic_energetic">Enthusiastic & Energetic</option>
                                        <option value="empathetic_understanding">Empathetic & Understanding</option>
                                    </Select>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Applied to all new proposals by default
                                    </p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">
                                        Emoji Policy
                                    </label>
                                    <Select
                                        value={emojiPolicy}
                                        onChange={(e) => setEmojiPolicy(e.target.value)}
                                    >
                                        <option value="off">Off - No emojis</option>
                                        <option value="light">Light - Subtle use</option>
                                        <option value="playful">Playful - Frequent use</option>
                                    </Select>
                                </div>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="font-medium text-foreground">Strict Compliance Mode</p>
                                        <p className="text-sm text-muted-foreground">
                                            Always scan for contact info and off-platform requests
                                        </p>
                                    </div>
                                    <Toggle checked={complianceStrict} onChange={setComplianceStrict} />
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>

                    {/* Connected Profiles */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.2 }}
                    >
                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between">
                                <CardTitle className="flex items-center gap-3 text-lg">
                                    <LinkIcon className="w-5 h-5 text-primary" />
                                    Connected Profiles
                                </CardTitle>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => navigate('/profiles')}
                                >
                                    Manage Profiles
                                </Button>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                {profiles.length === 0 ? (
                                    <div className="text-center py-6">
                                        <p className="text-sm text-muted-foreground mb-3">
                                            No Upwork profiles connected yet
                                        </p>
                                        <Button
                                            variant="outline"
                                            onClick={() => navigate('/profiles')}
                                        >
                                            Connect Profile
                                        </Button>
                                    </div>
                                ) : (
                                    profiles.map((profile: { _id: string; profileLabel: string; upworkProfileUrl: string; lastSyncAt?: number }) => (
                                        <div
                                            key={profile._id}
                                            className="flex items-center justify-between p-3 rounded-lg bg-muted"
                                        >
                                            <div>
                                                <p className="font-medium text-foreground">
                                                    {profile.profileLabel}
                                                </p>
                                                <p className="text-sm text-muted-foreground">
                                                    {profile.upworkProfileUrl}
                                                </p>
                                            </div>
                                            {profile.lastSyncAt ? (
                                                <Badge variant="success">
                                                    Synced {new Date(profile.lastSyncAt).toLocaleDateString()}
                                                </Badge>
                                            ) : (
                                                <Badge variant="outline">Never synced</Badge>
                                            )}
                                        </div>
                                    ))
                                )}
                            </CardContent>
                        </Card>
                    </motion.div>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    {/* Notifications */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.3 }}
                    >
                        <Card id="notifications">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-3 text-lg">
                                    <Bell className="w-5 h-5 text-primary" />
                                    Notifications
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <span className="text-sm text-foreground">Email updates</span>
                                        <p className="text-xs text-muted-foreground">Weekly summaries</p>
                                    </div>
                                    <Toggle checked={emailUpdates} onChange={setEmailUpdates} />
                                </div>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <span className="text-sm text-foreground">Usage alerts</span>
                                        <p className="text-xs text-muted-foreground">Token limits</p>
                                    </div>
                                    <Toggle checked={usageAlerts} onChange={setUsageAlerts} />
                                </div>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <span className="text-sm text-foreground">New features</span>
                                        <p className="text-xs text-muted-foreground">Product updates</p>
                                    </div>
                                    <Toggle checked={newFeatures} onChange={setNewFeatures} />
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>

                    {/* Security */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.4 }}
                    >
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-3 text-lg">
                                    <Shield className="w-5 h-5 text-primary" />
                                    Security
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {/* Connected Auth Methods */}
                                <div className="space-y-2">
                                    <p className="text-sm font-medium text-foreground">Connected Accounts</p>

                                    {/* Google */}
                                    <div className="flex items-center justify-between p-2 rounded-lg bg-muted">
                                        <div className="flex items-center gap-2">
                                            <GoogleIcon className="w-4 h-4" />
                                            <span className="text-sm">Google</span>
                                        </div>
                                        {linkedAccounts?.hasGoogle ? (
                                            <Badge variant="success" className="text-xs">
                                                <Check className="w-3 h-3 mr-1" />
                                                Connected
                                            </Badge>
                                        ) : (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleLinkProvider('google')}
                                                className="h-7 text-xs"
                                            >
                                                Link
                                            </Button>
                                        )}
                                    </div>

                                    {/* Email/Password */}
                                    <div className="flex items-center justify-between p-2 rounded-lg bg-muted">
                                        <div className="flex items-center gap-2">
                                            <Mail className="w-4 h-4" />
                                            <span className="text-sm">Email & Password</span>
                                        </div>
                                        {linkedAccounts?.hasPassword ? (
                                            <Badge variant="success" className="text-xs">
                                                <Check className="w-3 h-3 mr-1" />
                                                Set
                                            </Badge>
                                        ) : (
                                            <Badge variant="outline" className="text-xs">
                                                <X className="w-3 h-3 mr-1" />
                                                Not set
                                            </Badge>
                                        )}
                                    </div>
                                </div>

                                {/* Password Management */}
                                <div className="pt-2 border-t border-border">
                                    {linkedAccounts?.hasPassword ? (
                                        <Button
                                            variant="secondary"
                                            className="w-full"
                                            onClick={handleChangePassword}
                                        >
                                            <Key className="w-4 h-4 mr-2" />
                                            Change Password
                                        </Button>
                                    ) : (
                                        <>
                                            <Button
                                                variant="secondary"
                                                className="w-full"
                                                onClick={handleSetPassword}
                                            >
                                                <Key className="w-4 h-4 mr-2" />
                                                Set a Password
                                            </Button>
                                            <p className="text-xs text-muted-foreground text-center mt-2">
                                                You signed in with {linkedAccounts?.hasGoogle ? 'Google' : 'OAuth'}
                                            </p>
                                        </>
                                    )}
                                </div>

                                {/* Sessions */}
                                <div className="pt-2 border-t border-border">
                                    <Button
                                        variant="outline"
                                        className="w-full"
                                        onClick={handleSignOutAllDevices}
                                        disabled={isSigningOutAll}
                                    >
                                        <LogOut className="w-4 h-4 mr-2" />
                                        {isSigningOutAll ? 'Signing out...' : 'Sign out of all devices'}
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>
                </div>
            </div>

            {/* Save button */}
            <div className="flex items-center justify-end gap-4">
                {saveMessage && (
                    <span className={`text-sm ${saveMessage.includes('success') || saveMessage.includes('Signed out') ? 'text-primary' : 'text-destructive'}`}>
                        {saveMessage}
                    </span>
                )}
                <Button onClick={handleSave} disabled={isSaving}>
                    {isSaving ? 'Saving...' : 'Save Changes'}
                </Button>
            </div>

            {/* Password Modal */}
            <ChangePasswordModal
                isOpen={showPasswordModal}
                onClose={() => setShowPasswordModal(false)}
                isSettingNew={isSettingNewPassword}
            />
        </div>
    );
}
